import { useDocument, useFields } from '@modusign/react-signing';
import {
  createInitialLastInsertedField,
  useSigningState,
  useUIState,
} from '@modusign/react-signing/setup';
import { SigningState } from '@modusign/react-signing/types';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import useUploadDocumentFile from '@app/hooks/documentSetup/useUploadDocumentFile';
import { usePDF } from '@app/hooks/usePDF';
import { removeFieldOverPageRange } from '@app/libs/fields/removeFieldPageOver';
import getExtensionFromFileName from '@app/libs/file/getExtensionFromFileName';
import { getPdfTotalPage } from '@app/libs/pdf/getPdfPageLength';
import { setDataLayerValues } from '@app/store/analytics/actions';
import { modalOpen } from '@app/store/modal/actions';
import {
  templateCreateLoadPdfSuccess,
  templateFileChangeLoadPdfSuccess,
  templateFileChangePdfInitialize,
} from '@app/store/template/actions';

export default function useTemplateDocumentFileUpload(
  startFrom:
    | 'createTemplate'
    | 'templateFileChange'
    | 'embedded'
    | 'drafts'
    | 'createTemplateFromEmbeddedDraft'
) {
  const dispatch = useDispatch();
  const history = useHistory();

  const { document } = useDocument();
  const { initialize } = useSigningState();
  const { fields, removeField } = useFields();
  const { setPDF, resetPDF } = usePDF();
  const { ui } = useUIState();

  const { isLoading, mutate } = useUploadDocumentFile({
    onError: (error) => {
      dispatch(modalOpen('FILE_UPLOAD_ERROR_MODAL', { error }));
    },

    onSuccess: async ({ fileId, pdfArrayBuffer, url, title }, file) => {
      if (startFrom !== 'templateFileChange') {
        dispatch(
          setDataLayerValues('템플릿생성', {
            파일형식: getExtensionFromFileName(file.name).replace('.', ''),
            업로드파일출처: [
              'url' in file && '샘플문서',
              'blob' in file && '초안',
              '일반업로드',
            ].find(Boolean),
          })
        );
      }

      if (['templateFileChange', 'createTemplateFromEmbeddedDraft'].includes(startFrom)) {
        initialize({
          document: {
            ...document,
            pdfFile: {
              id: fileId,
              url,
            },
          },
          ui: {
            lastInsertedField: createInitialLastInsertedField(),
            insertingField: null,
            isInOrder: ui.isInOrder,
            // TODO : react-signing에서 state노출시 deep readonly 삭제하면 as 제거
          } as SigningState['ui']['setup'],
        });
        const pdfTotalPage = await getPdfTotalPage(pdfArrayBuffer);
        /**
         * pdf로드후 pdfContainer는 재로드를 하지 않아서, 한번 null로 세팅 후 재로드를 해줘야함
         */
        resetPDF();
        setPDF(pdfArrayBuffer);
        removeFieldOverPageRange(fields, pdfTotalPage, removeField);
      }

      // eslint-disable-next-line default-case
      switch (startFrom) {
        case 'createTemplate':
        case 'drafts':
        case 'embedded': {
          dispatch(templateCreateLoadPdfSuccess({ fileId, pdfArrayBuffer, title, url }));
          history.push('/template/setup/participant-setup');
          break;
        }
        case 'templateFileChange': {
          dispatch(templateFileChangePdfInitialize());
          dispatch(templateFileChangeLoadPdfSuccess({ fileId, pdfArrayBuffer, title, url }));
          break;
        }
      }
    },
  });

  return {
    isLoading,
    mutate,
  };
}
